import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import TemplateRow from "./TemplateRow";
import { StorageNotificationTemplate } from "../typeConfig";
import { useStyles } from "./styles";
import { PAGE_SIZE } from "./constants";

type Props = {
  templates: StorageNotificationTemplate[];
  deletingId: string | null;
  togglingDefaultId: string | null;
  onDelete: (template: StorageNotificationTemplate) => void;
  onToggleDefault: (id: string, isCurrentDefault: boolean) => void;
};

export default function TemplateListSection({
  templates,
  deletingId,
  togglingDefaultId,
  onDelete,
  onToggleDefault,
}: Props) {
  const { t } = useTranslation("storage");
  const classes = useStyles();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);

  const filteredTemplates = !searchQuery.trim()
    ? templates
    : templates.filter(t =>
        t.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
      );

  const totalPages = Math.ceil(filteredTemplates.length / PAGE_SIZE);
  const noResults = filteredTemplates.length === 0;
  const searchable = templates.length > PAGE_SIZE;

  useEffect(() => {
    if (page > 0 && page >= totalPages) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (!searchable && searchOpen) {
      setSearchOpen(false);
      setSearchQuery("");
    }
  }, [searchable, searchOpen]);

  const pagedTemplates = filteredTemplates.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const toggleSearch = () => {
    setSearchOpen(prev => {
      if (prev) {
        setSearchQuery("");
        setPage(0);
      }
      return !prev;
    });
  };

  if (templates.length === 0) return null;

  return (
    <>
      <div className={classes.listSubHeader}>
        <Typography
          variant="caption"
          color="textSecondary"
          className={classes.listSubHeaderLabel}
        >
          {t("notificationTemplates.list.templatesLabel")}
        </Typography>
        {searchable && (
          <Tooltip
            arrow
            title={
              searchOpen
                ? t("notificationTemplates.list.closeSearch")
                : t("notificationTemplates.list.search")
            }
          >
            <IconButton
              size="small"
              onClick={toggleSearch}
              data-testid="storage-notification-templates__search-toggle"
            >
              {searchOpen ? <CloseIcon /> : <SearchIcon />}
            </IconButton>
          </Tooltip>
        )}
      </div>

      {searchOpen && (
        <TextField
          autoFocus
          fullWidth
          size="small"
          variant="outlined"
          placeholder={t("notificationTemplates.list.searchPlaceholder")}
          value={searchQuery}
          onChange={handleSearch}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          data-testid="storage-notification-templates__search-input"
        />
      )}

      {noResults ? (
        <div className={classes.empty}>
          <Typography variant="body2" color="textSecondary">
            {t("notificationTemplates.list.noResults", {
              query: searchQuery,
            })}
          </Typography>
        </div>
      ) : (
        <>
          <div className={classes.list}>
            {pagedTemplates.map(template => (
              <TemplateRow
                key={template.id}
                template={template}
                deleting={template.id === deletingId}
                togglingDefault={template.id === togglingDefaultId}
                onDelete={onDelete}
                onToggleDefault={onToggleDefault}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className={classes.pagination}>
              <IconButton
                size="small"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" color="textSecondary">
                {page + 1} / {totalPages}
              </Typography>
              <IconButton
                size="small"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </div>
          )}
        </>
      )}
    </>
  );
}
